<template>
  <gaia-modal
    :style="styleVars"
    :show="props.show"
    width="460px"
    :mask-closable="false"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <template #header>
      <n-flex align="center" :size="11" :wrap="false">
        <div class="mark">
          <svg
            width="15"
            height="15"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M2.5 4h11M5.5 4V2.8h5V4M4 4l.7 9h6.6L12 4" />
          </svg>
        </div>
        <gaia-text size="subtitle" weight="semibold" tone="ink">
          Delete {{ itemsToDelete.length === 1 ? 'item' : `${itemsToDelete.length} items` }}?
        </gaia-text>
      </n-flex>
    </template>

    <n-flex vertical :size="14">
      <gaia-banner type="error"> {{ confirmationText }} This cannot be undone. </gaia-banner>

      <!-- Naming what goes is the whole safeguard here, so the list is shown
           rather than summarised — it scrolls once it outgrows the dialog. -->
      <n-flex v-if="itemsToDelete.length > 0" vertical :size="6">
        <gaia-section-header>Will be deleted</gaia-section-header>
        <div class="items">
          <n-flex
            v-for="item in itemsToDelete"
            :key="item.key"
            align="center"
            :size="9"
            :wrap="false"
            class="item"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="item-icon"
            >
              <path v-if="item.type === 'directory'" d="M2 4h4.5l1.5 2H14v6.5H2z" />
              <template v-else-if="item.type === 'contract'">
                <path d="M3.5 1.8h6l3 3v9.4h-9z" />
                <path d="M6 8.5h4M6 11h4" />
              </template>
              <template v-else>
                <path d="M3.5 1.8h6l3 3v9.4h-9z" />
                <path d="M9 1.8v3.2h3.2" />
              </template>
            </svg>
            <gaia-text truncate size="control" tone="text" :title="item.name">
              {{ item.name }}
            </gaia-text>
          </n-flex>
        </div>
      </n-flex>

      <gaia-banner v-for="(error, index) in deletionErrors" :key="index" type="error">
        {{ error }}
      </gaia-banner>
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="8">
        <gaia-button size="small" :disabled="isDeleting" @click="closeModal">Cancel</gaia-button>
        <gaia-button type="error" size="small" :loading="isDeleting" @click="handleConfirm">
          Delete
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useVaultStore, type VaultItem } from '@/stores/vaultStore';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSectionHeader from '@/components/design/base/GaiaSectionHeader.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import { gaiaNeutral, gaiaStatus } from '@/components/design/themes/tokens';
import { extractErrorDetail } from '@/utils/errorHandler';
import { describeVaultCounts, groupVaultKeys } from '@/utils/vaultSelection';

// Props
const props = defineProps<{
  show: boolean;
  selectedItems: string[];
  /** Names for keys the vault store hasn't loaded — a page outside the vault listing. */
  itemNames?: Record<string, string>;
}>();

// Emits
const emit = defineEmits<{
  'update:show': [value: boolean];
  deleted: [];
}>();

// Store
const vaultStore = useVaultStore();

// Local state
const isDeleting = ref<boolean>(false);
const deletionErrors = ref<string[]>([]);

// Computed
const FALLBACK: Record<VaultItem['type'], string> = {
  directory: 'Folder',
  contract: 'Contract',
  file: 'File',
};

function typeOfKey(key: string): VaultItem['type'] {
  if (key.startsWith('dir-')) return 'directory';
  if (key.startsWith('contract-')) return 'contract';
  return 'file';
}

const itemsToDelete = computed<VaultItem[]>(() => {
  return props.selectedItems.map(key => {
    const item = vaultStore.getItemByKey(key);
    const name = props.itemNames?.[key] ?? FALLBACK[typeOfKey(key)];
    return item ?? { key, name, type: typeOfKey(key) };
  });
});

const groups = computed(() => groupVaultKeys(props.selectedItems));

const confirmationText = computed<string>(() => {
  const counts = describeVaultCounts({
    files: groups.value.fileUids.length,
    contracts: groups.value.contractUids.length,
    folders: groups.value.directoryUids.length,
  });
  // What goes along that the list doesn't name.
  const consequences = [
    groups.value.contractUids.length > 0 ? 'A contract goes with all of its files.' : null,
    groups.value.directoryUids.length > 0
      ? 'A folder goes with everything inside it, contracts included.'
      : null,
  ].filter(Boolean);
  return [`Are you sure you want to delete ${counts}?`, ...consequences].join(' ');
});

// Methods
function closeModal(): void {
  emit('update:show', false);
  resetForm();
}

function resetForm(): void {
  deletionErrors.value = [];
  isDeleting.value = false;
}

async function handleConfirm(): Promise<void> {
  if (props.selectedItems.length === 0) return;

  isDeleting.value = true;
  deletionErrors.value = [];

  try {
    const deletePromises: Promise<void>[] = [];

    for (const uid of groups.value.directoryUids) {
      deletePromises.push(vaultStore.deleteDirectory(uid));
    }
    for (const uid of groups.value.contractUids) {
      deletePromises.push(vaultStore.deleteContract(uid));
    }
    for (const uid of groups.value.fileUids) {
      deletePromises.push(vaultStore.deleteFile(uid));
    }

    // Execute all deletions
    await Promise.all(deletePromises);

    emit('deleted');
    closeModal();
  } catch (error) {
    const detail = extractErrorDetail(error, 'Failed to delete selected items');
    // A review project's data room (or a folder around one) can't be deleted
    // while the project exists; point the user at the way out.
    deletionErrors.value.push(
      detail.includes("review project's data room")
        ? `${detail} Delete the review project first.`
        : detail
    );
  } finally {
    isDeleting.value = false;
  }
}
/**
 * Inline custom properties on the modal card rather than `v-bind()`. This
 * component's root is a Teleport, so Vue has no root element in place to
 * write `v-bind()` vars onto and every colour bound that way resolved to
 * nothing. On the card they cascade to header, body and footer alike.
 */
const styleVars = {
  '--vdel-item-border': gaiaNeutral.borderSoft,
  '--vdel-mark-color': gaiaStatus.alert,
  '--vdel-mark-surface': gaiaStatus.alertSurface,
  '--vdel-subtle-color': gaiaNeutral.textSubtle,
};
</script>

<style scoped>
.mark {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: var(--vdel-mark-surface);
  color: var(--vdel-mark-color);
}

.items {
  max-height: 200px;
  overflow-y: auto;
}

.item {
  padding: 7px 0;
  border-bottom: 1px solid var(--vdel-item-border);
}

.item:last-child {
  border-bottom: none;
}

.item-icon {
  flex: none;
  color: var(--vdel-subtle-color);
}
</style>
